
import React, { useEffect, useState } from 'react';
import AppLayout from '@/components/layout/AppLayout';
import { AlertsList } from './components/AlertsList';
import { Bell, AlertCircle, InfoIcon } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useUser } from '@/contexts/UserContext';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useNavigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { supabase } from '@/integrations/supabase/client';
import { TeamVisibilitySection } from './components/TeamVisibilitySection';
import { AllTeamsMonthView } from './components/AllTeamsMonthView';
import { AllGuardsWeekView } from './components/AllGuardsWeekView';
import { CustomDateGuardView } from './components/CustomDateGuardView';

import { EmployeeScheduleViewer } from './components/EmployeeScheduleViewer';
import { format, startOfWeek } from 'date-fns';

const ScheduleAlerts = () => {
  const { t } = useLanguage();
  const { isAdmin, isLoading } = useUser();
  const navigate = useNavigate();
  const [adminChecked, setAdminChecked] = useState(false);
  const [directAdminCheck, setDirectAdminCheck] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);
  const [currentWeek, setCurrentWeek] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Double-check admin status directly with Supabase RPC
  useEffect(() => {
    const checkAdminDirectly = async () => {
      try {
        const { data: isAdminResult, error } = await supabase.rpc('is_admin', {
          user_id: (await supabase.auth.getUser()).data.user?.id
        });
        
        if (error) {
          console.error('Error checking admin status directly:', error);
        } else {
          console.log('Direct admin check result:', isAdminResult);
          setDirectAdminCheck(!!isAdminResult);
        }
      } catch (err) {
        console.error('Error in direct admin check:', err);
      } finally {
        setCheckingAdmin(false);
      }
    };
    
    checkAdminDirectly();
  }, []);

  // Redirect non-admin users
  useEffect(() => {
    if (!isLoading && !checkingAdmin) {
      console.log('Schedule Alerts: Admin status checked. isAdmin =', isAdmin, 'directAdminCheck =', directAdminCheck);
      setAdminChecked(true);
      
      // Allow access if either check confirms admin status
      if (!isAdmin && !directAdminCheck) {
        console.log('Non-admin user attempted to access Schedule Alerts');
        navigate('/');
      }
    }
  }, [isAdmin, isLoading, navigate, directAdminCheck, checkingAdmin]);

  if (isLoading || checkingAdmin) {
    return (
      <AppLayout>
        <div className="flex justify-center items-center h-full p-8">
          <p className="text-muted-foreground">{t('loading') || 'Loading...'}</p>
        </div>
      </AppLayout>
    );
  }

  const handleWeekChange = (date: Date) => {
    setCurrentWeek(startOfWeek(date, { weekStartsOn: 1 }));
  };

  const handleMonthChange = (date: Date) => {
    setCurrentMonth(date);
  };

  return (
    <AppLayout>
      <Toaster position="top-right" />
      <div className="h-full w-full max-w-full overflow-hidden">
        <div className="space-y-6 p-4 max-w-full">
          <div className="flex items-center justify-between border-b pb-4">
            <div className="flex items-center space-x-3">
              <Bell className="h-7 w-7 text-primary animate-pulse" />
              <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
                  {t('scheduleAlerts') || 'Schedule Alerts'}
                </h1>
                <p className="text-muted-foreground text-sm mt-1">
                  {t('manageTeamSchedules') || 'Manage team schedules and visibility'}
                </p>
              </div>
            </div>
          </div>
        
          {(isAdmin || directAdminCheck) ? (
            <>
              <Alert className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 shadow-sm">
                <InfoIcon className="h-5 w-5 text-blue-600" />
                <AlertTitle className="text-blue-800 font-semibold">
                  {t('aboutScheduleAlerts') || 'About Schedule Alerts'}
                </AlertTitle>
                <AlertDescription className="text-blue-700 leading-relaxed">
                  {t('scheduleAlertsDescription') || 
                    'Comprehensive schedule management dashboard. Monitor team visibility, track guard schedules, and manage alerts for optimal workforce planning.'}
                </AlertDescription>
              </Alert>
              
              {/* Team Visibility Section */}
              <div className="w-full max-w-full overflow-hidden">
                <TeamVisibilitySection currentWeek={currentWeek} onWeekChange={handleWeekChange} />
              </div>
              
              {/* Custom Date Guard View */}
              <div className="w-full max-w-full overflow-hidden">
                <CustomDateGuardView currentMonth={currentMonth} />
              </div>
              
              {/* All Teams Month View */}
              <div className="w-full max-w-full overflow-hidden">
                <AllTeamsMonthView currentMonth={currentMonth} />
              </div>
              
              {/* All Guards Week View */}
              <div className="w-full max-w-full overflow-hidden">
                <AllGuardsWeekView currentMonth={currentMonth} />
              </div>
              
              {/* Employee Schedule Viewer */}
              <div className="w-full max-w-full overflow-hidden">
                <EmployeeScheduleViewer />
              </div>
              
              {/* Alerts List */}
              <div className="w-full max-w-full overflow-hidden">
                <AlertsList />
              </div>
            </>
          ) : (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>
              {t('accessDenied') || 'Access Denied'}
            </AlertTitle>
            <AlertDescription>
              {t('adminOnlyFeature') || 'Schedule alerts are only available to administrators.'}
            </AlertDescription>
          </Alert>
          )}
        </div>
      </div>
    </AppLayout>
  );
};

export default ScheduleAlerts;
